import { FC, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGlobalModalContext } from "rujira.ui";
import {
  BanxaSellCapabilities,
  getBanxaSellCapabilities,
} from "../../services/banxa";
import {
  parseBanxaDeepLink,
  removeBanxaDeepLinkParam,
} from "../../services/banxaDeepLink";
import { usePreloadedBalances } from "./Balance";
import { getBanxaSellSources } from "../../services/banxaSellSources";
import { BanxaOffRamp } from "./BanxaOffRamp";
import { BanxaOnRamp } from "./BanxaOnRamp";

const EMPTY_SELL_CAPABILITIES: BanxaSellCapabilities = {
  cryptocurrencies: [],
  fiats: [],
};

export const BanxaDeepLinkHandler: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const balances = usePreloadedBalances(false);
  const balancesRef = useRef(balances);
  const handledSearch = useRef("");
  const { showModal, hideModal } = useGlobalModalContext();
  balancesRef.current = balances;

  useEffect(() => {
    if (!__BANXA_ENABLED__) return;

    const flow = parseBanxaDeepLink(location.search);
    if (!flow) {
      handledSearch.current = "";
      return;
    }
    if (handledSearch.current === location.search) return;

    const clearDeepLink = () =>
      navigate(
        {
          pathname: location.pathname,
          search: removeBanxaDeepLinkParam(location.search),
          hash: location.hash,
        },
        { replace: true }
      );

    if (flow === "buy") {
      handledSearch.current = location.search;
      showModal({
        className: "modal--lg",
        backgroundClose: false,
        // Opened with the page, often before the wallet has reconnected.
        keepOnAccountChange: true,
        children: <BanxaOnRamp onBack={hideModal} />,
      });
      clearDeepLink();
      return;
    }

    let active = true;
    getBanxaSellCapabilities()
      .catch(() => EMPTY_SELL_CAPABILITIES)
      .then((capabilities) => {
        if (!active || handledSearch.current === location.search) return;
        handledSearch.current = location.search;
        const initialSourceId =
          getBanxaSellSources(balancesRef.current, capabilities)[0]?.id || "";

        showModal({
          className: "modal--lg",
          backgroundClose: false,
          // Opened with the page, often before the wallet has reconnected.
          keepOnAccountChange: true,
          children: (
            <BanxaOffRamp
              capabilities={capabilities}
              initialSourceId={initialSourceId}
              onBack={hideModal}
            />
          ),
        });
        clearDeepLink();
      });

    return () => {
      active = false;
    };
  }, [
    hideModal,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    showModal,
  ]);

  return null;
};
